import {
  SecurityConfigurationCard,
  ValueAndActionWrapper,
} from "features/settings/common/styles";
import Title from "common/Typography/Title";
import DarkModeToggle from "common/Home/Header/DarkModeToggle";
import { useDeleteAccount } from "@shared/features/settings/hooks";
import { openMessage } from "utils/misc";
import DeleteAccountConfirmModal from "features/settings/Security/DeleteAccountConfirmModal";
import { IS_WEB_VIEW } from "common/constants";
import styled, { useTheme } from "styled-components";
import Button from "common/Buttons/Button";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { isIOS } from "react-device-detect";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";

const GeneralSettings = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    showDeleteAccountButton,
    onPressDeleteAccount,
    closeDeleteAccountModal,
    deleteAccountModalVisible,
    onDeleteAccountSuccess,
  } = useDeleteAccount({
    openMessage,
    isIos: IS_WEB_VIEW && isIOS,
  });

  const showDelete = showDeleteAccountButton && !IS_TELEGRAM_WEB_APP;
  const deleteAccountModal = (
    <DeleteAccountConfirmModal
      visible={deleteAccountModalVisible}
      closeModal={closeDeleteAccountModal}
      onSuccess={onDeleteAccountSuccess}
    />
  );

  // New look: preferences in one panel; deleting the account sits apart in
  // its own quiet panel, with red only on the action itself.
  if (theme.isNewLook) {
    return (
      <>
        <Panel title={t("general_settings.title")} flush>
          <ValueAndActionWrapper>
            <DarkModeToggle textStyle={NEW_LOOK_TOGGLE_LABEL_STYLE} />
          </ValueAndActionWrapper>
        </Panel>
        {showDelete && (
          <Panel title={t("general_settings.delete_account")} flush>
            <DangerRow>
              <DangerText>{t("delete_account_modal.content")}</DangerText>
              <Button danger={true} onClick={onPressDeleteAccount}>
                {t("general_settings.delete_account")}
              </Button>
            </DangerRow>
          </Panel>
        )}
        {deleteAccountModal}
      </>
    );
  }

  return (
    <SecurityConfigurationCard>
      <Title level={3}>{t("general_settings.title")}</Title>
      <ValueAndActionWrapper>
        <DarkModeToggle
          textStyle={{
            color: theme.colors.mediumText,
            fontSize: isBottomNavigator
              ? theme.typography.fontSizes.S
              : theme.typography.fontSizes.M,
          }}
        />
      </ValueAndActionWrapper>
      {showDelete && (
        <ValueAndActionWrapper onClick={onPressDeleteAccount}>
          <DeleteAccount>{t("general_settings.delete_account")}</DeleteAccount>
        </ValueAndActionWrapper>
      )}
      {deleteAccountModal}
    </SecurityConfigurationCard>
  );
};

export default GeneralSettings;

const NEW_LOOK_TOGGLE_LABEL_STYLE = { fontSize: 14, fontWeight: 500 };

const DeleteAccount = styled.div`
  cursor: pointer;
  color: ${(p) => p.theme.colors.red};
`;
const DangerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px 24px;
  padding: 16px 20px;
  @media (max-width: 500px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const DangerText = styled.div`
  max-width: 520px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
